import { getAdminRoleFromToken, isAdministratorRole, normalizeAdminRole } from './ecmwfExcelImport';

export const TRIP_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const BULK_DELETE_HIGH_RISK_THRESHOLD = 1000;

export const PREVIEW_COUNT_ROWS = [
    { previewKey: 'forecast_data_count', deletedKey: 'forecast_data' },
    { previewKey: 'check_claims_count', deletedKey: 'check_claims' },
    { previewKey: 'claims_count', deletedKey: 'claims' },
    { previewKey: 'forecast_not_triggered_count', deletedKey: 'forecast_not_triggered' },
];

/** Ordre métier de suppression (relations SQL). */
export const DELETION_TIMELINE_ROWS = [
    { previewKey: 'claims_count', deletedKey: 'claims', step: 1 },
    { previewKey: 'forecast_not_triggered_count', deletedKey: 'forecast_not_triggered', step: 2 },
    { previewKey: 'check_claims_count', deletedKey: 'check_claims', step: 3 },
    { previewKey: 'forecast_data_count', deletedKey: 'forecast_data', step: 4 },
];

export const getParisTodayIso = () => (
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
);

export const normalizePolicyCode = (value) => String(value || '').trim();

export const isPreviewFiltersMatch = ({
    tripDay,
    policyCode,
    previewTripDay,
    previewPolicyCode,
}) => (
    String(tripDay || '').trim() === String(previewTripDay || '').trim()
    && normalizePolicyCode(policyCode) === normalizePolicyCode(previewPolicyCode)
);

export const isValidTripDay = (value) => {
    const trimmed = String(value || '').trim();
    if (!TRIP_DAY_PATTERN.test(trimmed)) return false;
    const date = new Date(`${trimmed}T12:00:00`);
    return !Number.isNaN(date.getTime());
};

export const isOrakleRole = (role) => normalizeAdminRole(role) === 'orakle';

export const canAccessCheckForecastBulkDelete = (role) => (
    isAdministratorRole(role) || isOrakleRole(role)
);

export const getPreviewTotal = (previewResponse) => (
    Number(previewResponse?.total_records_to_delete) || 0
);

export const hasBulkDeletePreviewData = (previewResponse) => (
    getPreviewTotal(previewResponse) > 0
);

export const getPreviewCounts = (previewResponse) => {
    const preview = previewResponse?.preview || {};
    return PREVIEW_COUNT_ROWS.map(({ previewKey }) => ({
        key: previewKey,
        value: Number(preview[previewKey]) || 0,
    }));
};

export const getDeletedCounts = (deleteResponse) => {
    const deleted = deleteResponse?.deleted || {};
    return PREVIEW_COUNT_ROWS.map(({ deletedKey }) => ({
        key: deletedKey,
        value: Number(deleted[deletedKey]) || 0,
    }));
};

export const getTimelineCounts = (response, mode = 'preview') => {
    const bucket = mode === 'preview' ? response?.preview : response?.deleted;
    return DELETION_TIMELINE_ROWS.map(({ previewKey, deletedKey, step }) => {
        const field = mode === 'preview' ? previewKey : deletedKey;
        return {
            step,
            key: field,
            deletedKey,
            value: Number(bucket?.[field]) || 0,
        };
    });
};

export const isHighRiskBulkDelete = (total) => (
    Number(total) > BULK_DELETE_HIGH_RISK_THRESHOLD
);

export const isBulkDeleteConfirmValid = ({
    confirmChecked,
    confirmText,
    tripDay,
    total,
}) => {
    if (!confirmChecked) return false;
    if (!isHighRiskBulkDelete(total)) return true;
    const normalized = String(confirmText || '').trim();
    const day = String(tripDay || '').trim();
    return normalized === 'DELETE' || normalized === day;
};

const decodeJwtPayload = (token) => {
    try {
        const payload = token?.split('.')?.[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = decodeURIComponent(
            window.atob(normalized)
                .split('')
                .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
                .join('')
        );
        return JSON.parse(decoded);
    } catch {
        return null;
    }
};

export const getAdminEmailFromToken = (token) => {
    const payload = decodeJwtPayload(token);
    if (payload?.email) return payload.email;
    if (typeof payload?.sub === 'string' && payload.sub.includes('@')) return payload.sub;
    if (typeof window !== 'undefined') {
        return localStorage.getItem('admin_email') || '—';
    }
    return '—';
};

export const formatParisDateTime = (date = new Date()) => (
    new Intl.DateTimeFormat('fr-FR', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date)
);

export const BULK_DELETE_IMPORT_ACCEPT = '.csv,.xlsx,.xls';
export const BULK_DELETE_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

export const validateBulkDeleteImportFile = (file) => {
    if (!file) return 'fileRequired';
    const name = String(file.name || '').toLowerCase();
    const allowed = ['.csv', '.xlsx', '.xls'];
    if (!allowed.some((ext) => name.endsWith(ext))) return 'invalidType';
    if (file.size > BULK_DELETE_IMPORT_MAX_BYTES) return 'tooLarge';
    return null;
};

export const downloadBulkDeleteImportTemplate = () => {
    const header = 'trip_day,policy_code\n';
    const sample = '2026-07-29,pol_123456789\n';
    const blob = new Blob([header + sample], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'check_forecast_bulk_delete_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const getRoleDisplayLabel = (role) => {
    const normalized = normalizeAdminRole(role);
    if (isAdministratorRole(role)) return 'Admin';
    if (normalized === 'orakle') return 'Orakle';
    return role || '—';
};

export const getBulkDeleteErrorMessage = (error, labels = {}) => {
    const data = error?.response?.data;
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg || item?.message || String(item))
            .join(', ');
    }
    if (detail && typeof detail === 'object') {
        return detail.message || detail.reason || JSON.stringify(detail);
    }
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;

    const status = error?.response?.status;
    if (status === 400) return labels.invalidDate || 'Date trip_day invalide (format YYYY-MM-DD).';
    if (status === 401) return labels.unauthorized || 'Session expirée. Veuillez vous reconnecter.';
    if (status === 403) return labels.forbidden || 'Accès refusé — rôle insuffisant.';
    if (status === 500) return labels.server || 'Erreur serveur. Aucune suppression partielle n\'a été effectuée.';
    return error?.message || labels.default || 'Une erreur est survenue.';
};

export const resolveAdminRole = (accessToken) => (
    getAdminRoleFromToken(accessToken || (typeof window !== 'undefined'
        ? localStorage.getItem('admin_access_token')
        : null))
);

/** Réinitialise preview / suppression lorsque trip_day change. */
export const resetBulkDeleteUiState = (state) => ({
    ...state,
    preview: null,
    previewTripDay: '',
    previewPolicyCode: '',
    deleteResult: null,
    previewError: null,
    deleteError: null,
    showConfirm: false,
    confirmChecked: false,
    confirmText: '',
});
